import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useInventory } from '../contexts/InventoryContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useStockAnalytics } from '../hooks/useStockAnalytics';
import { useInventoryStats } from '../hooks/useInventoryStats';
import { supabase } from '../lib/supabase';
import { getAdminId } from '../utils/getAdminId';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ActivityLogger } from '../components/ui/ActivityLogger';
import { StockChart } from '../components/charts/StockChart';
import { NotesWidget } from '../components/dashboard/NotesWidget';
import { BarcodeScanner } from '../components/ui/BarcodeScanner';
import { CategoryAutocomplete } from '../components/ui/CategoryAutocomplete';
import {
  Package,
  TrendingUp,
  AlertTriangle,
  Users,
  DollarSign,
  BarChart3,
  ShoppingCart,
  Truck,
  FileText,
  Camera,
  Plus,
  ArrowRight,
  CheckCircle,
  Clock,
  Target,
  Zap,
  Crown,
  Building,
  Calendar,
  Eye,
  RefreshCw,
  Pause,
  Play,
  X,
  Search,
  Loader2,
  Save
} from 'lucide-react';

const AUTO_REFRESH_INTERVAL = 30000;

export const Dashboard: React.FC = () => {
  const { user, subscription, isOffline } = useAuth();
  const { items, shipments, isLoading, refreshInventory } = useInventory();
  const { t, language } = useLanguage();
  const { analytics, loading: analyticsLoading, refreshAnalytics } = useStockAnalytics(user);
  const { stats: inventoryStats, loading: statsLoading, refreshStats } = useInventoryStats(user);
  const navigate = useNavigate();

  const [greeting, setGreeting] = useState('');
  const [userLogo, setUserLogo] = useState<string | null>(null);
  const [trackingStats, setTrackingStats] = useState({
    active: 0,
    delivered: 0,
    returned: 0
  });
  const [shipmentStats, setShipmentStats] = useState({
    incoming: 0,
    outgoing: 0
  });

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshCountdown, setRefreshCountdown] = useState(AUTO_REFRESH_INTERVAL / 1000);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [showScannerModal, setShowScannerModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showQuickReceiveModal, setShowQuickReceiveModal] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState<string | null>(null);
  const [scannedItem, setScannedItem] = useState<any>(null);

  const [newItemForm, setNewItemForm] = useState({
    name: '',
    category: '',
    quantity: '',
    location: '',
    barcode: '',
    buy_price: '',
    sell_price: ''
  });
  const [addItemLoading, setAddItemLoading] = useState(false);

  const [quickReceiveForm, setQuickReceiveForm] = useState({
    supplier: '',
    barcode: '',
    quantity: ''
  });
  const [quickReceiveLoading, setQuickReceiveLoading] = useState(false);

  const [lowStockItems, setLowStockItems] = useState<Array<{id: string; name: string; quantity: number; category: string}>>([]);
  const [recentItems, setRecentItems] = useState<Array<{id: string; name: string; category: string; quantity: number; created_at: string; location?: string; sell_price?: number}>>([]);

  useEffect(() => {
    // Set greeting based on time of day and language
    const hour = new Date().getHours();
    let timeGreeting = '';

    if (hour < 12) {
      switch(language) {
        case 'fr':
          timeGreeting = 'Bonjour';
          break;
        case 'es':
          timeGreeting = 'Buenos días';
          break;
        case 'nl':
          timeGreeting = 'Goedemorgen';
          break;
        default:
          timeGreeting = 'Good morning';
      }
    } else if (hour < 18) {
      switch(language) {
        case 'fr':
          timeGreeting = 'Bon après-midi';
          break;
        case 'es':
          timeGreeting = 'Buenas tardes';
          break;
        case 'nl':
          timeGreeting = 'Goedemiddag';
          break;
        default:
          timeGreeting = 'Good afternoon';
      }
    } else {
      switch(language) {
        case 'fr':
          timeGreeting = 'Bonsoir';
          break;
        case 'es':
          timeGreeting = 'Buenas noches';
          break;
        case 'nl':
          timeGreeting = 'Goedenavond';
          break;
        default:
          timeGreeting = 'Good evening';
      }
    }

    setGreeting(timeGreeting);
  }, [language]);

  useEffect(() => {
    // Load user's company logo
    const loadUserLogo = async () => {
      if (user?.id) {
        try {
          // First check if user has logo_url in their profile
          if (user?.logo_url) {
            setUserLogo(user.logo_url);
            return;
          }

          // Otherwise fetch from database
          const { data: userData } = await supabase
            .from('users')
            .select('logo_url')
            .eq('id', user.id)
            .maybeSingle();

          if (userData?.logo_url) {
            setUserLogo(userData.logo_url);
          }
        } catch (error) {
          console.error('Failed to load user logo:', error);
        }
      }
    };

    loadUserLogo();
  }, [user?.id, user?.logo_url]);

  useEffect(() => {
    const loadShipmentStats = async () => {
      if (!user) return;

      try {
        const adminId = getAdminId(user);

        const [
          { data: trackedShipments },
          { data: incomingShipments },
          { data: outgoingShipments }
        ] = await Promise.all([
          supabase
            .from('outgoing_shipments')
            .select('tracking_status')
            .eq('admin_id', adminId)
            .not('tracking_code', 'is', null),
          supabase
            .from('incoming_shipments')
            .select('id')
            .eq('admin_id', adminId),
          supabase
            .from('outgoing_shipments')
            .select('id')
            .eq('admin_id', adminId)
        ]);

        if (trackedShipments) {
          const active = trackedShipments.filter(s =>
            ['pending_pickup', 'in_transit', 'out_for_delivery'].includes(s.tracking_status)
          ).length;

          const delivered = trackedShipments.filter(s =>
            s.tracking_status === 'delivered'
          ).length;

          const returned = trackedShipments.filter(s =>
            ['returned', 'failed_delivery'].includes(s.tracking_status)
          ).length;

          setTrackingStats({ active, delivered, returned });
        }

        setShipmentStats({
          incoming: incomingShipments?.length || 0,
          outgoing: outgoingShipments?.length || 0
        });
      } catch (error) {
        console.error('Error loading shipment stats:', error);
      }
    };

    loadShipmentStats();
  }, [user]);

  useEffect(() => {
    const fetchLowStockAndRecentItems = async () => {
      if (!user) return;
      const adminId = getAdminId(user);
      if (!adminId) return;

      try {
        const [lowStockResult, recentResult] = await Promise.all([
          supabase
            .from('products')
            .select('id, name, quantity, category')
            .eq('admin_id', adminId)
            .gt('quantity', 0)
            .lte('quantity', 10)
            .order('quantity', { ascending: true })
            .limit(5),
          supabase
            .from('products')
            .select('id, name, category, quantity, created_at, location, sell_price')
            .eq('admin_id', adminId)
            .order('created_at', { ascending: false })
            .limit(5)
        ]);

        if (lowStockResult.data) {
          setLowStockItems(lowStockResult.data);
        }
        if (recentResult.data) {
          setRecentItems(recentResult.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard items:', error);
      }
    };

    fetchLowStockAndRecentItems();
  }, [user]);

  const loadShipmentStats = useCallback(async () => {
    if (!user) return;
    try {
      const adminId = getAdminId(user);
      const [
        { data: trackedShipments },
        { data: incomingShipments },
        { data: outgoingShipments }
      ] = await Promise.all([
        supabase
          .from('outgoing_shipments')
          .select('tracking_status')
          .eq('admin_id', adminId)
          .not('tracking_code', 'is', null),
        supabase
          .from('incoming_shipments')
          .select('id')
          .eq('admin_id', adminId),
        supabase
          .from('outgoing_shipments')
          .select('id')
          .eq('admin_id', adminId)
      ]);
      if (trackedShipments) {
        const active = trackedShipments.filter(s =>
          ['pending_pickup', 'in_transit', 'out_for_delivery'].includes(s.tracking_status)
        ).length;
        const delivered = trackedShipments.filter(s => s.tracking_status === 'delivered').length;
        const returned = trackedShipments.filter(s =>
          ['returned', 'failed_delivery'].includes(s.tracking_status)
        ).length;
        setTrackingStats({ active, delivered, returned });
      }
      setShipmentStats({
        incoming: incomingShipments?.length || 0,
        outgoing: outgoingShipments?.length || 0
      });
    } catch (error) {
      console.error('Error loading shipment stats:', error);
    }
  }, [user]);

  const performRefresh = useCallback(async () => {
    if (isOffline || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.all([
        refreshInventory?.(),
        refreshAnalytics?.(),
        refreshStats?.(),
        loadShipmentStats()
      ]);
      setLastRefresh(new Date());
      setRefreshCountdown(AUTO_REFRESH_INTERVAL / 1000);
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isOffline, isRefreshing, refreshInventory, refreshAnalytics, refreshStats, loadShipmentStats]);

  useEffect(() => {
    if (autoRefreshEnabled && !isOffline) {
      refreshIntervalRef.current = setInterval(() => {
        performRefresh();
      }, AUTO_REFRESH_INTERVAL);

      countdownIntervalRef.current = setInterval(() => {
        setRefreshCountdown(prev => (prev > 0 ? prev - 1 : AUTO_REFRESH_INTERVAL / 1000));
      }, 1000);
    }
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [autoRefreshEnabled, isOffline, performRefresh]);

  const handleManualRefresh = () => {
    performRefresh();
  };

  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled(prev => !prev);
    if (!autoRefreshEnabled) {
      setRefreshCountdown(AUTO_REFRESH_INTERVAL / 1000);
    }
  };

  const handleBarcodeScan = async (barcode: string) => {
    setScannedBarcode(barcode);
    const existingItem = items.find(item =>
      item.barcode === barcode || item.sku === barcode || item.batch_number === barcode
    );
    if (existingItem) {
      setScannedItem(existingItem);
    } else {
      setScannedItem(null);
      setNewItemForm(prev => ({ ...prev, barcode }));
      setShowAddItemModal(true);
    }
    setShowScannerModal(false);
  };

  const handleAddItem = async () => {
    if (!newItemForm.name.trim() || !user) return;
    setAddItemLoading(true);
    try {
      const adminId = getAdminId(user);
      const { error } = await supabase.from('products').insert({
        admin_id: adminId,
        name: newItemForm.name.trim(),
        category: newItemForm.category.trim() || null,
        quantity: parseInt(newItemForm.quantity) || 0,
        location: newItemForm.location.trim() || null,
        barcode: newItemForm.barcode.trim() || null,
        cost_price: parseFloat(newItemForm.buy_price) || null,
        sell_price: parseFloat(newItemForm.sell_price) || null
      });
      if (error) throw error;
      setShowAddItemModal(false);
      setNewItemForm({ name: '', category: '', quantity: '', location: '', barcode: '', buy_price: '', sell_price: '' });
      performRefresh();
    } catch (error) {
      console.error('Error adding item:', error);
    } finally {
      setAddItemLoading(false);
    }
  };

  const handleQuickReceive = async () => {
    if (!quickReceiveForm.barcode.trim() || !user) return;
    setQuickReceiveLoading(true);
    try {
      const existingItem = items.find(item =>
        item.barcode === quickReceiveForm.barcode || item.sku === quickReceiveForm.barcode
      );
      if (existingItem) {
        const newQuantity = existingItem.quantity + (parseInt(quickReceiveForm.quantity) || 1);
        await supabase
          .from('products')
          .update({ quantity: newQuantity })
          .eq('id', existingItem.id);
      }
      setShowQuickReceiveModal(false);
      setQuickReceiveForm({ supplier: '', barcode: '', quantity: '' });
      performRefresh();
    } catch (error) {
      console.error('Error receiving stock:', error);
    } finally {
      setQuickReceiveLoading(false);
    }
  };

  const allCategories = [...new Set(items.map(item => item.category).filter(Boolean))] as string[];

  const stats = {
    totalItems: inventoryStats.totalItems,
    totalQuantity: inventoryStats.totalQuantity,
    lowStock: inventoryStats.lowStock,
    outOfStock: inventoryStats.outOfStock,
    categories: inventoryStats.categories,
    costValue: inventoryStats.costValue,
    sellValue: inventoryStats.sellValue,
    totalValue: inventoryStats.totalValue,
    potentialRevenue: inventoryStats.sellValue,
    incomingShipments: shipmentStats.incoming,
    outgoingShipments: shipmentStats.outgoing,
  };

  const quickActions = [
    {
      title: t('dashboard.scanBarcode'),
      description: t('dashboard.quickBarcodeScanning'),
      icon: Camera,
      onClick: () => setShowScannerModal(true),
      color: 'from-cyan-500 to-teal-500',
      textColor: 'text-cyan-600',
      bgColor: 'bg-cyan-50 dark:bg-cyan-900/20'
    },
    {
      title: t('dashboard.addNewItem'),
      description: t('dashboard.addItemsToInventory'),
      icon: Plus,
      onClick: () => setShowAddItemModal(true),
      color: 'from-blue-500 to-sky-500',
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20'
    },
    {
      title: t('nav.payment'),
      description: t('dashboard.processSales'),
      icon: ShoppingCart,
      onClick: () => navigate('/payment-terminal'),
      color: 'from-green-500 to-emerald-500',
      textColor: 'text-green-600',
      bgColor: 'bg-green-50 dark:bg-green-900/20'
    },
    {
      title: t('dashboard.createInvoice'),
      description: t('dashboard.generateSalesInvoice'),
      icon: FileText,
      onClick: () => navigate('/sales-invoices'),
      color: 'from-orange-500 to-amber-500',
      textColor: 'text-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20'
    },
    {
      title: t('dashboard.incomingShipment'),
      description: t('dashboard.receiveNewStock'),
      icon: Truck,
      onClick: () => setShowQuickReceiveModal(true),
      color: 'from-teal-500 to-cyan-500',
      textColor: 'text-teal-600',
      bgColor: 'bg-teal-50 dark:bg-teal-900/20'
    },
    {
      title: t('dashboard.viewReports'),
      description: t('dashboard.analyticsInsights'),
      icon: BarChart3,
      onClick: () => navigate('/reports'),
      color: 'from-amber-500 to-yellow-500',
      textColor: 'text-amber-600',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-4">
          {/* Company Logo or Default GetInv Logo */}
          <div className="flex-shrink-0">
            <img
              src={userLogo || "/ChatGPT Image Oct 8, 2025, 03_35_44 AM copy.png"}
              alt={userLogo ? "Company Logo" : "GetInv"}
              crossOrigin="anonymous"
              className="h-16 w-16 object-contain rounded-lg"
            />
          </div>

          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {greeting}, {user?.username}!
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {t('dashboard.welcomeMessage')}
            </p>
            {user?.company_name && (
              <div className="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
                <Building className="h-4 w-4 mr-2" />
                {user.company_name}
              </div>
            )}
          </div>
        </div>

        {/* Status Indicators */}
        <div className="flex flex-wrap items-center gap-3 mt-4 lg:mt-0">
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
            {isOffline ? (
              <div className="flex items-center text-red-600 dark:text-red-400">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <span className="text-sm font-medium">{t('dashboard.offlineMode')}</span>
              </div>
            ) : (
              <div className="flex items-center text-green-600 dark:text-green-400">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span className="text-sm font-medium">{t('dashboard.systemOnline')}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing || isOffline}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              title="Refresh now"
            >
              <RefreshCw className={`h-4 w-4 text-gray-600 dark:text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />
            <button
              onClick={toggleAutoRefresh}
              disabled={isOffline}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              title={autoRefreshEnabled ? 'Pause auto-refresh' : 'Enable auto-refresh'}
            >
              {autoRefreshEnabled ? (
                <Pause className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              ) : (
                <Play className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              )}
            </button>
            {autoRefreshEnabled && !isOffline && (
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums min-w-[24px]">
                {refreshCountdown}s
              </span>
            )}
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400">
            <Clock className="h-3 w-3 inline mr-1" />
            {lastRefresh.toLocaleTimeString()}
          </div>

          {user?.role === 'super_admin' && (
            <Link to="/super-admin">
              <Button variant="secondary" size="sm">
                <Crown className="h-4 w-4 mr-2" />
                {t('dashboard.superAdminPanel')}
              </Button>
            </Link>
          )}

          {user?.role === 'admin' && (
            <Link to="/admin/users">
              <Button variant="secondary" size="sm">
                <Crown className="h-4 w-4 mr-2" />
                {t('dashboard.userManagement')}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.totalItems')}</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {stats.totalItems}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.totalQuantity')}</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {stats.totalQuantity.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="bg-yellow-100 dark:bg-yellow-900/30 p-3 rounded-full">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.lowStock')}</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {stats.lowStock}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full">
              <Target className="h-6 w-6 text-blue-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.categories')}</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {stats.categories}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link to="/package-tracing" className="card p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              📦 Package Tracking
            </h3>
            <ArrowRight className="h-5 w-5 text-primary-600" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="h-2 w-2 bg-blue-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Active</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {trackingStats.active}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="h-2 w-2 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Delivered</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {trackingStats.delivered}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="h-2 w-2 bg-orange-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Returned</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {trackingStats.returned}
              </span>
            </div>
          </div>
        </Link>

        {user?.pricing_enabled && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                💰 {t('dashboard.inventoryValue')}
              </h3>
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.costValue')}:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  €{stats.costValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.sellValue')}:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  €{stats.sellValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('dashboard.potentialProfit')}:</span>
                <span className="font-bold text-green-600 dark:text-green-400">
                  €{(stats.sellValue - stats.costValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              📦 {t('dashboard.shipments')}
            </h3>
            <Truck className="h-5 w-5 text-blue-600" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.incoming')}:</span>
              <span className="font-medium text-blue-600 dark:text-blue-400">
                {stats.incomingShipments}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.outgoing')}:</span>
              <span className="font-medium text-blue-500 dark:text-blue-400">
                {stats.outgoingShipments}
              </span>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              📊 {t('dashboard.systemStatus')}
            </h3>
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.status')}:</span>
              <span className="font-medium text-green-600 dark:text-green-400">
                {isOffline ? '🔴 Offline' : '🟢 Online'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.role')}:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {user?.role}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
          <Zap className="h-5 w-5 inline mr-2 text-amber-500" />
          {t('dashboard.quickActions')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                key={index}
                onClick={action.onClick}
                className="group p-4 rounded-2xl transition-all duration-300 border border-gray-200 dark:border-gray-700 hover:shadow-xl hover:scale-105 hover:-translate-y-1 text-left relative overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${action.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                <div className="relative flex flex-col items-center text-center space-y-3">
                  <div className={`p-3 rounded-xl ${action.bgColor} group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`h-6 w-6 ${action.textColor}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-1">
                      {action.title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      {action.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Items */}
        <div className="lg:col-span-2">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                📦 {t('dashboard.recentInventoryItems')}
              </h2>
              <Link to="/inventory">
                <Button variant="secondary" size="sm">
                  <Eye className="h-4 w-4 mr-2" />
                  {t('dashboard.viewAll')}
                </Button>
              </Link>
            </div>

            {recentItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Package className="h-12 w-12 mx-auto mb-4" />
                <p>{t('dashboard.noInventoryItems')}</p>
                <Link to="/inventory">
                  <Button variant="primary" className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('dashboard.addFirstItem')}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mr-4">
                        <Package className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">
                          {item.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {item.category} • {item.location || t('dashboard.noLocation')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className={`text-lg font-semibold ${
                        item.quantity === 0 
                          ? 'text-red-600 dark:text-red-400'
                          : item.quantity <= 10
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {item.quantity}
                      </div>
                      {user?.pricing_enabled && item.sell_price && (
                        <div className="text-sm text-green-600 dark:text-green-400">
                          €{item.sell_price.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Content */}
        <div className="space-y-6">
          {/* Low Stock Alert */}
          {lowStockItems.length > 0 && (
            <div className="card p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  ⚠️ {t('dashboard.lowStockAlert')}
                </h3>
              </div>
              <div className="space-y-3">
                {lowStockItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <div>
                      <div className="font-medium text-yellow-800 dark:text-yellow-300 text-sm">
                        {item.name}
                      </div>
                      <div className="text-xs text-yellow-600 dark:text-yellow-400">
                        {item.category}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-yellow-700 dark:text-yellow-300">
                        {item.quantity}
                      </div>
                      <div className="text-xs text-yellow-600 dark:text-yellow-400">
                        {t('dashboard.left')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/inventory">
                <Button variant="secondary" size="sm" className="w-full mt-4">
                  {t('dashboard.manageStockLevels')}
                </Button>
              </Link>
            </div>
          )}

        </div>
      </div>

      {/* Team Notes and Activity Log - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <NotesWidget />
        <div className="card p-6 h-[400px] overflow-hidden">
          <ActivityLogger maxItems={6} showUserColumn={false} />
        </div>
      </div>

      {/* Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <StockChart
          data={analytics.monthlyTrends}
          type="incoming"
          title={t('dashboard.incomingStock')}
        />
        <StockChart
          data={analytics.monthlyTrends}
          type="outgoing"
          title={t('dashboard.outgoingStock')}
        />
        <StockChart
          data={analytics.monthlyTrends}
          type="total"
          title={t('dashboard.totalInventory')}
        />
      </div>

      {/* System Information */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
          🎯 {t('dashboard.systemInformation')}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <Users className="h-5 w-5 text-blue-600 mr-2" />
              <h3 className="font-medium text-blue-800 dark:text-blue-300">{t('dashboard.account')}</h3>
            </div>
            <div className="text-sm space-y-1">
              <div>{t('dashboard.role')}: <span className="font-medium">{user?.role}</span></div>
              <div>{t('dashboard.plan')}: <span className="font-medium">{subscription?.plan_type || 'Basic'}</span></div>
              {subscription?.expires_at && (
                <div>{t('dashboard.expires')}: <span className="font-medium">
                  {new Date(subscription.expires_at).toLocaleDateString()}
                </span></div>
              )}
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <h3 className="font-medium text-green-800 dark:text-green-300">{t('dashboard.features')}</h3>
            </div>
            <div className="text-sm space-y-1">
              <div>{t('dashboard.pricing')}: <span className={user?.pricing_enabled ? 'text-green-600' : 'text-gray-600'}>
                {user?.pricing_enabled ? `✅ ${t('dashboard.enabled')}` : `🚫 ${t('dashboard.disabled')}`}
              </span></div>
              <div>{t('dashboard.taxRate')}: <span className="font-medium">
                {user?.tax_percentage || 21}%
              </span></div>
              <div>{t('dashboard.paymentTerms')}: <span className="font-medium">
                {user?.payment_terms_days || 14} {t('dashboard.days')}
              </span></div>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <Calendar className="h-5 w-5 text-amber-600 mr-2" />
              <h3 className="font-medium text-amber-800 dark:text-amber-300">{t('dashboard.usage')}</h3>
            </div>
            <div className="text-sm space-y-1">
              <div>{t('dashboard.itemsAdded')}: <span className="font-medium">{stats.totalItems}</span></div>
              <div>{t('dashboard.shipments')}: <span className="font-medium">
                {stats.incomingShipments + stats.outgoingShipments}
              </span></div>
              <div>{t('dashboard.categories')}: <span className="font-medium">{stats.categories}</span></div>
            </div>
          </div>
        </div>
      </div>

      <BarcodeScanner
        isOpen={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        onScan={handleBarcodeScan}
        title={t('dashboard.scanBarcode')}
      />

      {scannedItem && scannedBarcode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Item Found</h3>
              <button
                onClick={() => { setScannedItem(null); setScannedBarcode(null); }}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{scannedItem.name}</p>
                  <p className="text-sm text-gray-500">{scannedItem.category}</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Stock:</span>
                  <span className={`ml-2 font-semibold ${scannedItem.quantity <= 10 ? 'text-amber-600' : 'text-gray-900 dark:text-gray-100'}`}>
                    {scannedItem.quantity}
                  </span>
                </div>
                {scannedItem.location && (
                  <div>
                    <span className="text-gray-500">Location:</span>
                    <span className="ml-2 text-gray-900 dark:text-gray-100">{scannedItem.location}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => { setScannedItem(null); setScannedBarcode(null); }}
              >
                Close
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={() => { navigate('/inventory'); }}
              >
                View in Inventory
              </Button>
            </div>
          </div>
        </div>
      )}

      {showAddItemModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                <Plus className="h-5 w-5 mr-2 text-blue-600" />
                {t('dashboard.addNewItem')}
              </h3>
              <button
                onClick={() => setShowAddItemModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <Input
                  value={newItemForm.name}
                  onChange={(e) => setNewItemForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Product name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <CategoryAutocomplete
                    value={newItemForm.category}
                    onChange={(val) => setNewItemForm(prev => ({ ...prev, category: val }))}
                    categories={allCategories}
                    placeholder="Category"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Quantity
                  </label>
                  <Input
                    type="number"
                    value={newItemForm.quantity}
                    onChange={(e) => setNewItemForm(prev => ({ ...prev, quantity: e.target.value }))}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Location
                  </label>
                  <Input
                    value={newItemForm.location}
                    onChange={(e) => setNewItemForm(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Shelf A1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Barcode
                  </label>
                  <Input
                    value={newItemForm.barcode}
                    onChange={(e) => setNewItemForm(prev => ({ ...prev, barcode: e.target.value }))}
                    placeholder="123456789"
                  />
                </div>
              </div>
              {user?.pricing_enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Buy Price
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newItemForm.buy_price}
                      onChange={(e) => setNewItemForm(prev => ({ ...prev, buy_price: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Sell Price
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newItemForm.sell_price}
                      onChange={(e) => setNewItemForm(prev => ({ ...prev, sell_price: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setShowAddItemModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleAddItem}
                disabled={addItemLoading || !newItemForm.name.trim()}
              >
                {addItemLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Add Item
              </Button>
            </div>
          </div>
        </div>
      )}

      {showQuickReceiveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                <Truck className="h-5 w-5 mr-2 text-teal-600" />
                Quick Receive Stock
              </h3>
              <button
                onClick={() => setShowQuickReceiveModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Supplier (optional)
                </label>
                <Input
                  value={quickReceiveForm.supplier}
                  onChange={(e) => setQuickReceiveForm(prev => ({ ...prev, supplier: e.target.value }))}
                  placeholder="Supplier name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Item Barcode / SKU *
                </label>
                <div className="flex gap-2">
                  <Input
                    value={quickReceiveForm.barcode}
                    onChange={(e) => setQuickReceiveForm(prev => ({ ...prev, barcode: e.target.value }))}
                    placeholder="Scan or enter barcode"
                    className="flex-1"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowQuickReceiveModal(false);
                      setShowScannerModal(true);
                    }}
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quantity to Add
                </label>
                <Input
                  type="number"
                  value={quickReceiveForm.quantity}
                  onChange={(e) => setQuickReceiveForm(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder="1"
                  min="1"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This will add quantity to the existing item matching the barcode. For detailed receiving, use the full Incoming Shipments page.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setShowQuickReceiveModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleQuickReceive}
                disabled={quickReceiveLoading || !quickReceiveForm.barcode.trim()}
              >
                {quickReceiveLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Receive Stock
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};